/**
 * API Client - Phase 11+ Compliant
 *
 * Uses canonical apiClient from api/client.js with:
 * - Automatic X-Idempotency-Key injection for mutations
 * - X-Correlation-Id capture
 * - Bearer token authentication
 * - Response envelope handling
 * - 401/403/429 handling
 *
 * NOTE: X-Admin-ID header REMOVED as of Phase 3
 * NOTE: All axios usage consolidated into api/client.js
 */
import apiClient, {
    api as apiWithIdem,
    getLastCorrelationId,
} from "../api/client";

// Use empty string for relative paths (proxy handles routing)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API_BASE = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

// Export canonical apiClient with full features
export default apiClient;

// Export correlation ID getter
export { getLastCorrelationId };

// Export typed API methods with idempotency
export { apiWithIdem as apiIdem };

// Rate limit tracking for UI feedback
const rateLimitState = {
    retryAfter: {},
    listeners: new Set(),
};

export const subscribeToRateLimits = (callback) => {
    rateLimitState.listeners.add(callback);
    return () => rateLimitState.listeners.delete(callback);
};

export const isRateLimited = (endpointGroup) => {
    const until = rateLimitState.retryAfter[endpointGroup];
    if (!until) return false;
    if (Date.now() >= until) {
        delete rateLimitState.retryAfter[endpointGroup];
        return false;
    }
    return true;
};

export const getRateLimitRemaining = (endpointGroup) => {
    const until = rateLimitState.retryAfter[endpointGroup];
    if (!until) return 0;
    const remaining = Math.ceil((until - Date.now()) / 1000);
    return remaining > 0 ? remaining : 0;
};

// Add upload method for file uploads (uses canonical client's auth interceptor)
apiClient.upload = async (url, formData, options = {}) => {
    const config = {
        ...options,
        headers: {
            "Content-Type": "multipart/form-data",
            ...(options.headers || {}),
        },
    };
    const response = await apiClient.post(url, formData, config);
    // apiClient interceptor returns response directly (axios response object)
    return response.data || response;
};

export const generateIdempotencyKey = () => {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Extract items from response envelope
 * Handles both {data: [...]} and {ok: true, data: {items: [...]}} structures
 */
export const extractItems = (response, fallbackKey = null) => {
    if (!response) return [];
    let data = response.data || response;

    // Handle nested {ok: true, data: {...}} envelope (standard API response)
    if (data && data.ok !== undefined && data.data) {
        data = data.data;
    }

    if (Array.isArray(data.items)) return data.items;
    if (fallbackKey && Array.isArray(data[fallbackKey]))
        return data[fallbackKey];
    if (Array.isArray(data)) return data;
    for (const key of [
        "users",
        "games",
        "transactions",
        "logs",
        "providers",
        "promotions",
        "achievements",
        "methods",
    ]) {
        if (Array.isArray(data[key])) return data[key];
    }
    return [];
};

export const extractPagination = (response) => {
    let data = response?.data || response;

    // Handle nested {ok: true, data: {...}} envelope
    if (data && data.ok !== undefined && data.data) {
        data = data.data;
    }

    if (!data) return { total: 0, hasMore: false, nextCursor: null };
    return {
        total: data.total ?? null,
        hasMore: data.has_more ?? false,
        nextCursor: data.next_cursor ?? null,
    };
};

export const isMutationOk = (response) => {
    const data = response?.data || response;
    return data && data.ok === true;
};

export const getErrorMessage = (error) => {
    if (error.serverMessage) return error.serverMessage;
    if (error.response?.data?.message) return error.response.data.message;
    if (error.response?.data?.detail) return error.response.data.detail;
    if (error.message) return error.message;
    return "An error occurred";
};

export const safeApiCall = async (
    apiFunction,
    fallbackMessage = "Not available",
) => {
    try {
        const result = await apiFunction();
        // Extract data from axios response object
        const data = result?.data !== undefined ? result.data : result;
        return { success: true, data };
    } catch (error) {
        if (error.response?.status === 404) {
            return {
                success: false,
                notAvailable: true,
                message: fallbackMessage,
            };
        }
        const message = getErrorMessage(error);
        return {
            success: false,
            error: message,
            code: error.code || error.response?.data?.code,
            details: error.details || error.response?.data?.details,
            correlationId: error.correlationId,
        };
    }
};
